import { useState, type ReactNode } from 'react';
import { Image, Pressable, ScrollView, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, fontFamily, price, radii, space, type } from '~/ui/tokens';
import { formatIsoShort, formatUtcDateTime } from '../lib/dateUtils';
import { formatMoney } from '../lib/money';
import { TOTAL_LABEL } from '../lib/payment';
import { TaxAmount } from './TaxAmount';
import { formatTaxName } from '../lib/taxes';
import type { Money, Rate } from '../lib/api/types';
import { Button, ModalSheet } from './ui';

// "Sep 21 – Sep 23, 2026" — a single readable range instead of raw ISO dates.
function formatStayRange(checkin: string, checkout: string): string {
  const endYear = checkout.split('-')[0] ?? checkout.slice(0, 4);
  return `${formatIsoShort(checkin)} – ${formatIsoShort(checkout)}, ${endYear}`;
}

import { useCurrency } from '~/stores/useCurrency';

type RoomDetailSheetProps = {
  rate: Rate | null;
  mode?: 'room' | 'deal';
  nights?: number;
  checkin?: string;
  checkout?: string;
  guests?: number;
  onClose: () => void;
  onSelect: (rate: Rate) => void;
};

function totalFor(money: Money, nights: number): Money {
  return { ...money, amount: String(Number(money.amount) * Math.max(1, nights)) };
}

function totalForRate(rate: Rate, nights: number): Money {
  return rate.stayTotal ?? totalFor(rate.price, nights);
}

export function RoomDetailSheet({ rate, mode = 'room', nights = 1, checkin, checkout, guests = 2, onClose, onSelect }: RoomDetailSheetProps) {
  useCurrency((state) => state.selectedCurrency);
  return (
    <ModalSheet visible={rate !== null} onClose={onClose} size={mode === 'room' ? 'wide' : 'deal'}>
      {rate ? (
        mode === 'room' ? (
          <RoomDetails rate={rate} nights={nights} onClose={onClose} onSelect={onSelect} />
        ) : (
          <DealDetails rate={rate} nights={nights} checkin={checkin} checkout={checkout} guests={guests} onClose={onClose} onSelect={onSelect} />
        )
      ) : null}
    </ModalSheet>
  );
}

function ModalHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <View style={styles.header}>
      <Text style={styles.headerTitle}>{title}</Text>
      <Pressable accessibilityRole="button" accessibilityLabel="Close" onPress={onClose} style={styles.closeButton}>
        <Ionicons name="close" size={24} color={colors.ink} />
      </Pressable>
    </View>
  );
}

function RoomDetails({ rate, nights, onClose, onSelect }: { rate: Rate; nights: number; onClose: () => void; onSelect: (rate: Rate) => void }) {
  const { width, height } = useWindowDimensions();
  const wide = width >= 900;
  const [activeImage, setActiveImage] = useState(0);
  const amenities = rate.amenities?.slice(0, 14) ?? [];

  return (
    <View style={styles.roomSheet}>
      <ModalHeader title="Room details" onClose={onClose} />
      {/* ModalSheet bounds the sheet itself to the viewport (maxHeight:'100%'
          of a definite-height backdrop), but that constraint doesn't reliably
          reach a flex:1 descendant several levels down through an
          otherwise auto-height chain — roomBody could end up taller than
          the viewport with nothing to clip it, pushing the footer below the
          screen instead of leaving it for infoPane's own scroll to reach.
          An explicit pixel budget, read from the viewport itself, removes
          that ambiguity outright. -100 covers the header above plus the
          padded (non-bottom-sheet) dialog variant's own outer margin. */}
      <View style={[styles.roomBody, !wide && styles.roomBodyNarrow, !wide && { maxHeight: height - 100 }]}>
        <View style={[styles.galleryPane, !wide && styles.galleryPaneNarrow]}>
          {rate.images[activeImage] ? (
            <Image source={rate.images[activeImage]} resizeMode="cover" style={[styles.heroImage, !wide && styles.heroImageNarrow]} />
          ) : (
            <View style={[styles.heroImage, !wide && styles.heroImageNarrow, styles.imageEmpty]}>
              <Ionicons name="bed-outline" size={56} color={colors.inkFaint} />
              <Text style={styles.imageEmptyText}>Room photo not available</Text>
            </View>
          )}
          {rate.images.length > 1 ? (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.thumbRow}>
              {rate.images.map((image, index) => (
                <Pressable key={index} onPress={() => setActiveImage(index)} style={[styles.thumb, index === activeImage && styles.thumbActive]}>
                  <Image source={image} style={styles.thumbImage} />
                </Pressable>
              ))}
            </ScrollView>
          ) : null}
        </View>
        <View style={[styles.infoPane, !wide && styles.infoPaneNarrow]}>
          <ScrollView style={styles.infoScrollView} contentContainerStyle={styles.infoScroll}>
            <Text style={styles.roomSheetTitle}>{rate.roomGroupName || rate.roomName}</Text>
            <View style={styles.factList}>
              <Fact icon="bed-outline" label={rate.bedType || 'Bed configuration shown at booking'} />
              {rate.sleeps ? <Fact icon="people-outline" label={`Sleeps ${rate.sleeps}`} /> : null}
              <Fact icon="restaurant-outline" label={rate.meal.hasBreakfast ? 'Breakfast included' : 'Room only'} positive={rate.meal.hasBreakfast} />
              <Fact icon={rate.cancellation.freeCancellationBefore ? 'shield-checkmark-outline' : 'close-circle-outline'} label={rate.cancellation.freeCancellationBefore ? `Free cancellation until ${formatUtcDateTime(rate.cancellation.freeCancellationBefore)}` : 'Non-refundable'} positive={Boolean(rate.cancellation.freeCancellationBefore)} />
            </View>
            {amenities.length > 0 ? (
              <View style={styles.amenitiesBlock}>
                <Text style={styles.subheading}>Amenities</Text>
                <View style={styles.amenityGrid}>
                  {amenities.map((amenity) => (
                    <View key={amenity} style={styles.amenityItem}>
                      <Ionicons name="checkmark" size={15} color={colors.inkSoft} />
                      <Text style={styles.amenityText}>{amenity}</Text>
                    </View>
                  ))}
                </View>
              </View>
            ) : null}
          </ScrollView>
          <View style={styles.roomFooter}>
            <View>
              <Text style={styles.fromLabel}>From</Text>
              <Text style={styles.footerPrice}>{formatMoney(totalForRate(rate, nights))}</Text>
            </View>
            <Button label="View deal" onPress={() => onSelect(rate)} />
          </View>
        </View>
      </View>
    </View>
  );
}

function DealDetails({ rate, nights, checkin, checkout, guests, onClose, onSelect }: { rate: Rate; nights: number; checkin?: string; checkout?: string; guests: number; onClose: () => void; onSelect: (rate: Rate) => void }) {
  const total = totalForRate(rate, nights);
  const freeUntil = rate.cancellation.freeCancellationBefore;
  const chargeableTiers = rate.cancellation.tiers?.filter(tier => Number(tier.amountCharge.amount) > 0) ?? [];
  const nonIncludedTaxes = rate.taxes?.filter(t => !t.includedBySupplier) ?? [];
  return (
    <View style={styles.dealSheet}>
      <ModalHeader title="Room & rate details" onClose={onClose} />
      <ScrollView style={styles.dealScroll} contentContainerStyle={styles.dealScrollContent}>
        {/* Stay summary */}
        <View style={styles.dealSummary}>
          {rate.images[0] ? <Image source={rate.images[0]} style={styles.summaryImage} /> : <View style={[styles.summaryImage, styles.imageEmpty]}><Ionicons name="bed-outline" size={22} color={colors.inkFaint} /></View>}
          <View style={styles.summaryCopy}>
            <Text style={styles.summaryName}>{rate.roomGroupName || rate.roomName}</Text>
            <Text style={styles.summaryMeta}>{[rate.bedType, `${guests} guest${guests === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}</Text>
            {checkin && checkout ? <Text style={styles.summaryDates}>{formatStayRange(checkin, checkout)}</Text> : null}
            <Text style={styles.summaryNights}>{nights} night{nights === 1 ? '' : 's'}</Text>
          </View>
        </View>

        {/* Cancellation policy — the actual windows and penalties returned
            for this rate; never inferred or assumed. */}
        <Section title="Cancellation policy">
          {!freeUntil && !rate.cancellation.tiers?.length ? (
            <PolicyRow left="Non-refundable" right={formatMoney(total)} rightLabel="Cancellation charge" />
          ) : null}
          {freeUntil ? (
            <PolicyRow positive left="Free cancellation" leftDetail={`Until ${formatUtcDateTime(freeUntil)}`} right="Full refund" />
          ) : null}
          {/* A tier charging 0 is just the free-cancellation window restated —
              already covered by the "Free cancellation … Full refund" row
              above, so showing it again as its own line only reads as two
              rows disagreeing about the same cutoff date. */}
          {chargeableTiers.length ? (
            chargeableTiers.map((tier, idx) => (
              <PolicyRow
                key={idx}
                divided
                left={tier.startAt ? `From ${formatUtcDateTime(tier.startAt)}` : 'After the free cancellation deadline'}
                leftDetail={tier.endAt ? `Until ${formatUtcDateTime(tier.endAt)}` : undefined}
                right={formatMoney(tier.amountCharge)}
                rightLabel="Cancellation charge"
              />
            ))
          ) : freeUntil ? (
            <PolicyRow divided left="After the free cancellation deadline" right={formatMoney(total)} rightLabel="Full charge, no refund" />
          ) : null}
        </Section>

        {/* Payment */}
        <Section title="Payment">
          <View style={styles.paymentRow}>
            <Ionicons name={rate.paymentType === 'hotel' ? 'wallet-outline' : 'card-outline'} size={18} color={colors.inkSoft} />
            <View style={styles.paymentCopy}>
              <Text style={styles.paymentTitle}>{rate.paymentType === 'hotel' ? 'Pay at property' : 'Pay online'}</Text>
              <Text style={styles.paymentBody}>{rate.paymentType === 'hotel' ? 'Payment is collected by the property according to its accepted payment methods.' : 'Payment is collected securely during booking.'}</Text>
            </View>
          </View>
        </Section>

        {/* Price details */}
        <Section title="Price details">
          <PriceLine label={`${nights} night${nights === 1 ? '' : 's'} × 1 room`} value={formatMoney(total)} />
          {nonIncludedTaxes.length ? (
            nonIncludedTaxes.map((tax, idx) => (
              <PriceLine key={idx} label={`${formatTaxName(tax.name)} (payable at check-in)`} value={<TaxAmount amount={tax.amount} currencyCode={tax.currencyCode} textStyle={styles.sectionBody} />} />
            ))
          ) : (
            <PriceLine label="Taxes" value="Included" muted />
          )}
        </Section>
      </ScrollView>
      {/* The total lives in the fixed footer, next to Book, rather than as the
          last line of the scrollable Price details section — on a short
          viewport that section can scroll past the fold, and the total is
          the one figure a guest must see before booking. */}
      <View style={styles.dealFooter}>
        <View style={styles.totalLine}>
          <View style={styles.totalLeft}>
            <Text style={styles.totalText}>Total</Text>
            <Text style={styles.totalMeta}>{nights} night{nights === 1 ? '' : 's'}, 1 room, {guests} guest{guests === 1 ? '' : 's'}</Text>
          </View>
          <View style={styles.totalValueWrap}>
            <Text style={styles.totalText}>{formatMoney(total)}</Text>
            {/* Plain "Total" adds nothing once the price is shown, so the
                exclusion note takes its place; labels that carry meaning
                ("due now", "pay at hotel") stay. */}
            {nonIncludedTaxes.length && rate.paymentType === 'deposit' ? null : <Text style={[styles.totalMeta, styles.totalMetaEnd]}>{TOTAL_LABEL[rate.paymentType]}</Text>}
            {nonIncludedTaxes.length ? <Text style={[styles.totalMeta, styles.totalMetaEnd]}>Excludes amounts payable at the property</Text> : null}
          </View>
        </View>
        <Button label="Book" onPress={() => onSelect(rate)} />
      </View>
    </View>
  );
}

function PolicyRow({ left, leftDetail, right, rightLabel, positive, divided }: { left: string; leftDetail?: string; right: string; rightLabel?: string; positive?: boolean; divided?: boolean }) {
  return (
    <View style={[styles.policyRow, divided && styles.rowDivided]}>
      <View style={styles.policyRowLeft}>
        <View style={styles.policyRowPrimaryLine}>
          <Ionicons name={positive ? 'checkmark-circle' : 'close-circle-outline'} size={16} color={positive ? colors.green : colors.inkSoft} />
          <Text style={[styles.policyRowPrimary, positive && styles.policyRowPrimaryPositive]}>{left}</Text>
        </View>
        {leftDetail ? <Text style={styles.policyRowSecondary}>{leftDetail}</Text> : null}
      </View>
      <View style={styles.policyRowRight}>
        <Text style={[styles.policyRowAmount, positive && styles.policyRowAmountPositive]}>{right}</Text>
        {rightLabel ? <Text style={styles.policyRowLabel}>{rightLabel}</Text> : null}
      </View>
    </View>
  );
}

function Fact({ icon, label, positive }: { icon: keyof typeof Ionicons.glyphMap; label: string; positive?: boolean }) {
  return (
    <View style={styles.fact}>
      <Ionicons name={icon} size={19} color={positive ? colors.green : colors.ink} />
      <Text style={[styles.factText, positive && styles.factTextPositive]}>{label}</Text>
    </View>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <View style={styles.section}><Text style={styles.subheading}>{title}</Text>{children}</View>;
}

function PriceLine({ label, value, muted }: { label: string; value: ReactNode; muted?: boolean }) {
  return <View style={styles.priceLine}><Text style={[styles.sectionBody, muted && styles.rowMuted, styles.priceLabel]}>{label}</Text>{typeof value === 'string' ? <Text style={[styles.sectionBody, muted && styles.rowMuted]}>{value}</Text> : value}</View>;
}

const styles = StyleSheet.create({
  roomSheet: { flex: 1 },
  dealSheet: { maxHeight: '100%' },
  header: { minHeight: 60, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.card, borderBottomWidth: 1, borderBottomColor: colors.lineSoft, paddingHorizontal: space.cardLarge },
  headerTitle: { fontFamily: fontFamily.semibold, fontSize: 19, color: colors.ink, textAlign: 'center', paddingHorizontal: 36 },
  closeButton: { position: 'absolute', right: space.component, padding: space.related },
  roomBody: { flex: 1, flexDirection: 'row' },
  roomBodyNarrow: { flexDirection: 'column' },
  galleryPane: { flex: 1.65, backgroundColor: '#E8EAEC' },
  // Stacked (mobile): gallery and info both keeping a flex share of roomBody
  // left their split dependent on roomBody's own resolved height, which is
  // itself only bounded (not fixed) by the modal's maxHeight — the two sides
  // didn't reliably divide that ambiguity the same way on every render, and
  // could settle with info's box overlapping the bottom of the image instead
  // of sitting cleanly below it. Sizing the gallery to its own content
  // (image's minHeight + the thumbnail row) and leaving flex:1 solely on
  // infoPaneNarrow — so it alone claims whatever's left, scrollable — removes
  // that ambiguity instead of just tuning the ratio.
  galleryPaneNarrow: { minHeight: 300, flexGrow: 0, flexShrink: 0, flexBasis: 'auto' },
  heroImage: { flex: 1, width: '100%', minHeight: 260, backgroundColor: colors.lineSoft },
  // Stacked (mobile): galleryPaneNarrow no longer flexes against infoPane
  // (see below), so this flex:1 has no bounded parent height to resolve
  // against — react-native-web's <img> then falls back to the source
  // photo's own intrinsic pixel height instead of a predictable one, which
  // is what actually left too little of the modal's fixed budget for
  // infoPane and pushed its footer past the clipped edge. A fixed height
  // makes it exactly as tall as intended, independent of the source photo.
  heroImageNarrow: { flexGrow: 0, flexShrink: 0, flexBasis: 'auto', height: 260 },
  imageEmpty: { alignItems: 'center', justifyContent: 'center', gap: space.related },
  imageEmptyText: { fontFamily: fontFamily.medium, fontSize: type.bodyRegular.fontSize, color: colors.inkFaint },
  thumbRow: { backgroundColor: colors.card, gap: space.related, padding: space.related },
  thumb: { width: 76, height: 56, borderRadius: radii.sm, overflow: 'hidden', borderWidth: 2, borderColor: 'transparent' },
  thumbActive: { borderColor: colors.brand },
  thumbImage: { width: '100%', height: '100%' },
  infoPane: { flex: 1, minWidth: 350, borderLeftWidth: 1, borderLeftColor: colors.lineSoft },
  infoPaneNarrow: { minWidth: 0, borderLeftWidth: 0, minHeight: 320 },
  // flex:1 (not just the contentContainerStyle padding below) is what makes
  // this claim only the space infoPane has left after roomFooter, so long
  // content scrolls within that box instead of growing past it.
  infoScrollView: { flex: 1 },
  infoScroll: { padding: space.card, gap: space.compact },
  roomSheetTitle: { fontFamily: fontFamily.bold, ...type.pageTitle, color: colors.ink, letterSpacing: -0.25 },
  factList: { gap: space.related },
  fact: { flexDirection: 'row', alignItems: 'center', gap: space.related, minWidth: 0 },
  factText: { flexShrink: 1, fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft },
  factTextPositive: { fontFamily: fontFamily.medium, color: colors.green },
  amenitiesBlock: { borderTopWidth: 1, borderTopColor: colors.lineSoft, paddingTop: space.internal, gap: space.related },
  subheading: { fontFamily: fontFamily.semibold, fontSize: type.bodyLarge.fontSize, color: colors.ink },
  amenityGrid: { flexDirection: 'row', flexWrap: 'wrap', rowGap: space.related },
  amenityItem: { width: '50%', flexDirection: 'row', alignItems: 'flex-start', gap: space.micro, paddingRight: space.related },
  amenityText: { flex: 1, fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkSoft },
  roomFooter: { minHeight: 72, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.internal, borderTopWidth: 1, borderTopColor: colors.lineSoft, paddingHorizontal: space.component },
  fromLabel: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
  footerPrice: { fontFamily: fontFamily.bold, ...price.roomRate, color: colors.ink },
  dealScroll: { flexGrow: 0, flexShrink: 1 },
  dealScrollContent: { paddingBottom: space.related },
  // Stay summary
  dealSummary: { flexDirection: 'row', gap: space.internal, padding: space.card, borderBottomWidth: 1, borderBottomColor: colors.lineSoft },
  summaryImage: { width: 88, height: 88, borderRadius: radii.md, backgroundColor: colors.lineSoft },
  summaryCopy: { flex: 1, minWidth: 0, justifyContent: 'center', gap: 3 },
  summaryName: { fontFamily: fontFamily.semibold, ...type.cardTitle, color: colors.ink, marginBottom: 1 },
  summaryMeta: { fontFamily: fontFamily.regular, fontSize: 14, color: colors.inkSoft },
  summaryDates: { fontFamily: fontFamily.medium, fontSize: 14, color: colors.ink },
  summaryNights: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkFaint },
  // Shared section shell
  section: { gap: space.related, padding: space.card, borderBottomWidth: 1, borderBottomColor: colors.lineSoft },
  sectionBody: { fontFamily: fontFamily.regular, ...type.bodyLarge, color: colors.ink },
  rowDivided: { borderTopWidth: 1, borderTopColor: colors.lineSoft, paddingTop: space.internal, marginTop: space.micro },
  rowMuted: { fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkFaint },
  // Cancellation policy rows
  policyRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.internal },
  policyRowLeft: { flex: 1, minWidth: 0, gap: 5 },
  policyRowPrimaryLine: { flexDirection: 'row', alignItems: 'center', gap: space.micro },
  policyRowPrimary: { flexShrink: 1, fontFamily: fontFamily.medium, fontSize: 14, color: colors.ink },
  policyRowPrimaryPositive: { color: colors.green },
  policyRowSecondary: { marginLeft: 22, fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkFaint },
  policyRowRight: { alignItems: 'flex-end' },
  policyRowAmount: { fontFamily: fontFamily.medium, fontSize: 14, color: colors.ink },
  policyRowAmountPositive: { color: colors.green },
  policyRowLabel: { marginTop: 2, fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint },
  // Payment
  paymentRow: { flexDirection: 'row', alignItems: 'flex-start', gap: space.related },
  paymentCopy: { flex: 1, minWidth: 0, gap: space.micro },
  paymentTitle: { fontFamily: fontFamily.medium, fontSize: 14, color: colors.ink },
  paymentBody: { fontFamily: fontFamily.regular, fontSize: 13, lineHeight: 18, color: colors.inkSoft },
  // Price details
  priceLine: { flexDirection: 'row', justifyContent: 'space-between', gap: space.internal },
  priceLabel: { flex: 1, textTransform: 'capitalize' },
  totalLine: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.internal },
  // Both sides need flex:1/minWidth:0 — "Excludes amounts payable at the
  // property" is long enough on its own that, unconstrained, it dictated the
  // row's width instead of wrapping within it, pushing the price past the
  // modal's edge.
  // Left keeps its natural width (up to half the row) and the price column
  // takes the rest, so the short guest summary doesn't wrap into an orphan
  // while the long exclusion note wraps in the wider column.
  totalLeft: { flexShrink: 0, maxWidth: '50%' },
  totalValueWrap: { flex: 1, minWidth: 0, alignItems: 'flex-end' },
  totalMetaEnd: { textAlign: 'right' },
  totalText: { fontFamily: fontFamily.bold, ...price.roomRate, color: colors.ink },
  totalMeta: { marginTop: 2, fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint },
  // No border-top here: the scrollable content above always ends in a
  // <Section>, which already draws this exact hairline along its own
  // bottom edge — adding a second one here just doubled the line.
  dealFooter: { padding: space.card, gap: space.internal },
});
